import { FC, useMemo, useState, useEffect } from "react";
import { Box, TablePagination, TableRow, TableSortLabel } from "@mui/material";
import TableDefault from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import { Column, Order, RowData } from "../../types/ui";
import { getComparator } from "../../utils/table";
import SortArrow from "./SortArrow";
import StyledTableCell from "./StyledTableCell";
import StyledTableRow from "./StyledTableRow";

export interface Props {
  shouldHighlightOddRows?: boolean;
  columns: Column[];
  data: RowData[];
  dense?: boolean;
  nested?: boolean;
  hasPagination?: boolean;
  defaultOrderBy?: string;
  defaultOrder?: Order;
  headerHeight?: number | string;
  onPageChange?: (currentPage: number) => void;
  currentPage?: number;
  setCurrentPage?: (val: number) => void;
}

const Table: FC<Props> = ({
  columns,
  data = [],
  shouldHighlightOddRows,
  dense,
  nested,
  headerHeight,
  hasPagination = true,
  defaultOrder = "asc",
  defaultOrderBy,
  onPageChange,
  currentPage,
  setCurrentPage,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [orderBy, setOrderBy] = useState<keyof RowData>();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof RowData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const createSortHandler =
    (property: keyof RowData) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  const rows = useMemo(
    () =>
      order && orderBy ? [...data].sort(getComparator(order, orderBy)) : data,
    [data, order, orderBy]
  );

  useEffect(() => {
    const sortableColumns = columns
      .filter((c) => !!c.sortable)
      .map((c) => c.key);
    if (defaultOrderBy && sortableColumns.includes(defaultOrderBy)) {
      setOrderBy(defaultOrderBy);
    }
  }, [columns, defaultOrderBy]);

  useEffect(() => {
    if (defaultOrder) {
      setOrder(defaultOrder);
    }
  }, [defaultOrder]);

  useEffect(() => {
    if (currentPage !== undefined && currentPage >= 0) {
      setPage(currentPage);
      if (setCurrentPage) {
        setCurrentPage(-1);
      }
    }
  }, [currentPage, setCurrentPage]);

  return (
    <Box>
      <TableContainer component={Box}>
        <TableDefault sx={{ minWidth: { sm: 330, md: 400 } }}>
          <TableHead>
            <TableRow>
              {columns.map(
                (
                  {
                    key,
                    label,
                    sortable,
                    align = "left",
                    width,
                    textAlign,
                    ...rest
                  },
                  index
                ) => (
                  <StyledTableCell
                    key={key}
                    align={align}
                    textAlign={textAlign}
                    dense={dense}
                    nested={nested}
                    index={index}
                    subTableTitle={nested && !index}
                    width={width}
                    sortDirection={orderBy === key ? order : false}
                    headerHeight={headerHeight}
                    {...rest}
                  >
                    {sortable ? (
                      <TableSortLabel
                        hideSortIcon={false}
                        IconComponent={() => (
                          <SortArrow
                            direction={orderBy === key ? order : "asc"}
                          />
                        )}
                        active={orderBy === key}
                        direction={orderBy === key ? order : "asc"}
                        onClick={createSortHandler(key)}
                      >
                        {label}
                      </TableSortLabel>
                    ) : (
                      label
                    )}
                  </StyledTableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <StyledTableRow
                  key={row.id ?? index}
                  isOdd={shouldHighlightOddRows}
                >
                  {columns.map(
                    ({
                      key,
                      align,
                      textAlign,
                      sortable,
                      renderCell,
                      ...rest
                    }) => (
                      <StyledTableCell
                        key={key}
                        component="td"
                        scope="row"
                        align={align}
                        textAlign={textAlign}
                        dense={dense}
                        nested={nested}
                        sortable={sortable}
                        {...rest}
                      >
                        {renderCell ? renderCell(row) : row[key]}
                      </StyledTableCell>
                    )
                  )}
                </StyledTableRow>
              ))}
          </TableBody>
        </TableDefault>
      </TableContainer>

      {hasPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

export default Table;
